import { useMemo, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Segmented,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  Empty,
  Tooltip,
} from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { landingApi, type LandingDailyRow } from '@/api/landing.api';

const { Title, Text } = Typography;

const PAGE_LABEL: Record<string, string> = {
  index: '메인',
  download: '다운로드',
};

const PLATFORM_COLOR: Record<string, string> = {
  ios: 'blue',
  android: 'green',
  'desktop-mac': 'purple',
  'desktop-win': 'geekblue',
  'desktop-linux': 'gold',
  unknown: 'default',
};

export function LandingStatsPage() {
  const [days, setDays] = useState<number>(30);

  const statsQuery = useQuery({
    queryKey: ['landing-stats', days],
    queryFn: () => landingApi.stats(days),
  });

  const visitsQuery = useQuery({
    queryKey: ['landing-visits', 50],
    queryFn: () => landingApi.visits(50),
  });

  const dailyChartData = useMemo(() => {
    const rows = statsQuery.data?.daily ?? [];
    const byDay = new Map<string, { day: string; index: number; download: number }>();
    rows.forEach((r: LandingDailyRow) => {
      const key = dayjs(r.day).format('MM/DD');
      const cur = byDay.get(key) ?? { day: key, index: 0, download: 0 };
      if (r.page === 'index') cur.index += r.pv;
      if (r.page === 'download') cur.download += r.pv;
      byDay.set(key, cur);
    });
    return Array.from(byDay.values());
  }, [statsQuery.data]);

  const totals = statsQuery.data?.totals ?? [];
  const indexTotal = totals.find((t) => t.page === 'index');
  const downloadTotal = totals.find((t) => t.page === 'download');
  const totalPv = totals.reduce((sum, t) => sum + t.pv, 0);
  const totalUv = totals.reduce((sum, t) => sum + t.uv, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          랜딩 방문 통계
        </Title>
        <Segmented
          value={days}
          onChange={(v) => setDays(v as number)}
          options={[
            { label: '7일', value: 7 },
            { label: '30일', value: 30 },
            { label: '90일', value: 90 },
          ]}
        />
      </div>

      {statsQuery.isLoading ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="전체 PV" value={totalPv} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic title="전체 UV (sid 기준)" value={totalUv} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="메인 PV / UV"
                  value={`${indexTotal?.pv ?? 0} / ${indexTotal?.uv ?? 0}`}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card>
                <Statistic
                  title="다운로드 PV / UV"
                  value={`${downloadTotal?.pv ?? 0} / ${downloadTotal?.uv ?? 0}`}
                />
              </Card>
            </Col>
          </Row>

          <Card title={`일자별 PV (${days}일)`} style={{ marginBottom: 16 }}>
            {dailyChartData.length === 0 ? (
              <Empty description="데이터 없음" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <ReTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="index" name="메인" stroke="#1A73E8" strokeWidth={2} />
                  <Line type="monotone" dataKey="download" name="다운로드" stroke="#34A853" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Card title="플랫폼별 PV">
                <Table
                  size="small"
                  pagination={false}
                  rowKey="platform"
                  dataSource={statsQuery.data?.byPlatform ?? []}
                  columns={[
                    {
                      title: '플랫폼',
                      dataIndex: 'platform',
                      render: (v: string) => (
                        <Tag color={PLATFORM_COLOR[v] ?? 'default'}>{v}</Tag>
                      ),
                    },
                    {
                      title: 'PV',
                      dataIndex: 'pv',
                      align: 'right',
                      width: 100,
                    },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="유입 Referrer Top 20">
                <Table
                  size="small"
                  pagination={false}
                  rowKey="referrer"
                  dataSource={statsQuery.data?.topReferrers ?? []}
                  columns={[
                    {
                      title: 'Referrer',
                      dataIndex: 'referrer',
                      ellipsis: true,
                      render: (v: string) => (
                        <Tooltip title={v}>
                          <Text style={{ fontSize: 12 }}>{v}</Text>
                        </Tooltip>
                      ),
                    },
                    {
                      title: 'PV',
                      dataIndex: 'pv',
                      align: 'right',
                      width: 80,
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Card title="최근 방문 50건">
            <Table
              size="small"
              pagination={false}
              rowKey="id"
              loading={visitsQuery.isLoading}
              dataSource={visitsQuery.data ?? []}
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: '시각',
                  dataIndex: 'createdAt',
                  width: 160,
                  render: (v: string) => dayjs(v).format('MM/DD HH:mm:ss'),
                },
                {
                  title: '페이지',
                  dataIndex: 'page',
                  width: 100,
                  render: (v: string) => <Tag>{PAGE_LABEL[v] ?? v}</Tag>,
                },
                {
                  title: '플랫폼',
                  dataIndex: 'platform',
                  width: 110,
                  render: (v: string | null) =>
                    v ? <Tag color={PLATFORM_COLOR[v] ?? 'default'}>{v}</Tag> : '-',
                },
                {
                  title: 'IP',
                  dataIndex: 'ip',
                  width: 140,
                  render: (v: string | null) => v || '-',
                },
                {
                  title: 'Referrer',
                  dataIndex: 'referrer',
                  ellipsis: true,
                  render: (v: string | null) => v || <Text type="secondary">(direct)</Text>,
                },
                {
                  title: 'UA',
                  dataIndex: 'userAgent',
                  ellipsis: true,
                  render: (v: string | null) => (
                    <Tooltip title={v || ''}>
                      <Text style={{ fontSize: 11 }}>{v ?? '-'}</Text>
                    </Tooltip>
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </div>
  );
}
